import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { setBozpToken } from '@/api/bozp-token';
import { exchangeHandoff } from '@/api/training';
import { useT } from '@/i18n';
import { flushClientReports } from '@/lib/client-report';
import { parseHandoffParams } from '@/lib/handoff';
import { setSession } from '@/session';
import { Route as rootRoute } from './__root';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexPage,
});

/**
 * Exchange a given handoff code EXACTLY ONCE. The code is single-use (the server
 * marks it used and 401s a replay), but React StrictMode double-mounts the route in
 * dev - and any remount must not burn the code on a second call. Memoizing the promise
 * by code means both invocations await one server round-trip.
 */
const exchangeByCode = new Map<string, ReturnType<typeof exchangeHandoff>>();
function exchangeOnce(code: string) {
  let promise = exchangeByCode.get(code);
  if (!promise) {
    promise = exchangeHandoff(code);
    exchangeByCode.set(code, promise);
  }
  return promise;
}

function IndexPage() {
  const t = useT();
  const navigate = useNavigate();

  useEffect(() => {
    const params = parseHandoffParams(window.location.search);
    if (!params.handoff) {
      navigate({ to: '/not-found' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await exchangeOnce(params.handoff as string);
        if (cancelled) return;
        // Client-side navigation ONLY - the bozp token + session live in memory; a full
        // page reload (window.location) would wipe both and break every later call.
        setBozpToken(res.accessToken, res.expiresIn);
        // Anything the player reported before it had a token goes out now.
        flushClientReports();
        setSession({
          trainings: params.trainings,
          returnUrl: params.returnUrl,
          inquiryId: params.inquiryId,
          embed: params.embed,
          parentOrigin: params.parentOrigin,
        });
        navigate({ to: '/hub' });
      } catch {
        if (!cancelled) navigate({ to: '/not-found' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <main className="flex flex-1 flex-col items-center justify-center gap-5 p-10">
      <div className="flex items-center gap-2.5">
        <span aria-hidden className="h-5 w-5 rounded-[3px] bg-fixit-600" />
        <span className="font-semibold tracking-tight text-ink-strong">FixIt</span>
      </div>
      <span aria-hidden className="hb-spinner" />
      <p className="text-sm text-ink-soft">{t('Preparing your training…')}</p>
    </main>
  );
}
